import React, { memo, useMemo } from 'react';

import { useTranslation } from '@lyearn/i18n';
import { AddBlock } from '@lyearn/molecules';
import { Stack } from '@lyearn/ui';
import { getNewSelectFieldOption } from '@/modules/corporate/modules/database/utils/metadataField';
import { useDatabaseFormEditor } from '@/modules/corporate/modules/database/views/FormView/components/DatabaseFormEditor/context/DatabaseFormEditorContext';
import { DatabaseFormEditorActionEnum } from '@/modules/corporate/modules/database/views/FormView/components/DatabaseFormEditor/types';
import { DatabaseMetadataFieldType, Option } from '@/types/schema';

import SelectFormEditorOptionsItem from './components/SelectFormEditorOptionsItem';
import { SelectFormEditorOptionsListProps } from './types';

function SelectFormEditorOptionsList(props: SelectFormEditorOptionsListProps) {
  const { t } = useTranslation();
  const { onFormEditorAction } = useDatabaseFormEditor();

  const { allowPartialUpdates, field } = props;
  const options = useMemo(() => field.metadataField.options || [], [field.metadataField.options]);

  const methods = useMemo(() => {
    const onUpdate = (metadataField: Partial<DatabaseMetadataFieldType>) => {
      onFormEditorAction?.({
        type: DatabaseFormEditorActionEnum.UpdateField,
        payload: { fieldId: field.metadataField.fieldId, updates: { metadataField } },
      });
    };

    return {
      onAdd: () => {
        onUpdate({ options: [...options, getNewSelectFieldOption({ value: '' })] });
      },
      onDelete: (optionId: string) => {
        onUpdate({ options: options.filter((option) => option.optionId !== optionId) });
      },
      onChange: (updatedOption: Pick<Option, 'optionId' | 'optionText'>) => {
        onUpdate({
          options: options.map((option) =>
            option.optionId === updatedOption.optionId
              ? {
                  optionId: updatedOption.optionId,
                  value: updatedOption.optionText,
                  color: option.color,
                }
              : option,
          ),
        });
      },
    };
  }, [field.metadataField.fieldId, onFormEditorAction, options]);

  return (
    <Stack className="pt-8" direction="col" gapY={16}>
      {options?.map((option, index) => (
        <SelectFormEditorOptionsItem
          key={option.optionId}
          allowPartialUpdates={allowPartialUpdates}
          canDelete={options?.length > 1}
          index={index}
          option={{ optionId: option.optionId, optionText: option.value }}
          onChange={methods.onChange}
          onDelete={methods.onDelete}
        />
      ))}
      <AddBlock title={t('Database:AddOption')} onClick={methods.onAdd} />
    </Stack>
  );
}

export default memo(SelectFormEditorOptionsList);
